import blinkscribeapi from "../../api/blinkscribeapi";
import { requestSignMismatchConsent } from "./VisitConsentHost";
import { mismatchDetailsFromRefusal } from "../../notereview/noteVisit";
import { orderReviewAtSignOff } from "../OrderReview/OrderReviewHost";

/* Every care sign-off (Note Review, the note editor, the PDF viewer) goes
   through signOffNote. BlinkScribe refuses to sign a note that was recorded on
   another day than its visit while the patient had a visit that day
   (409 VISIT_MISMATCH, patient 8823 09-29: it would post into the old visit's
   Athena encounter). The provider is then asked twice (VisitConsentHost) and,
   only after both steps, the sign-off is sent again with
   confirm_visit_mismatch=true.

   preConfirmed: the caller already asked twice (Note Review asks before the
   first call when it can see the mismatch).
   Returns the server body; { cancelled: true } when the provider declined.
   Any other error is thrown exactly as before (the callers show its message).

   One sign-off per note at a time (2026-10-15, appt 655839): while a sign-off
   of a note is in flight from this tab (any screen), another call for the same
   note sends nothing and resolves to { inProgress: true, status: 409, ... }.
   BlinkScribe answers a second Sign Off the same way (409
   SIGNOFF_IN_PROGRESS, another tab or device); that answer resolves to the
   same shape instead of throwing. Callers show it as neutral info, not as an
   error: the first Sign Off is still running. */

export const VISIT_MISMATCH = "VISIT_MISMATCH";

export const isVisitMismatchRefusal = (err) =>
  err?.response?.status === 409 && err?.response?.data?.code === VISIT_MISMATCH;

export const SIGNOFF_IN_PROGRESS = "SIGNOFF_IN_PROGRESS";
export const SIGNOFF_IN_PROGRESS_MESSAGE = "Sign Off already in progress for this note.";

export const isSignOffInProgress = (err) =>
  err?.response?.status === 409 && err?.response?.data?.code === SIGNOFF_IN_PROGRESS;

const inProgress = (data) => ({
  status: 409,
  result: false,
  inProgress: true,
  code: SIGNOFF_IN_PROGRESS,
  message: data?.message || SIGNOFF_IN_PROGRESS_MESSAGE,
});

// document_name of every note this tab is signing right now.
const signingNow = new Set();

/* visit: { patientid, appointmentid } of the note (optional). When the visit's end-of-visit order review
   (OrderReviewHost, enabled providers only) is still not confirmed, the review screen opens again first;
   "Not yet" there signs nothing ({ cancelled: true }); Confirm or "Sign without these orders" signs as before.
   No pending review: nothing changes. */
export const signOffNote = async (body, { preConfirmed = false, visit = null } = {}) => {
  const key = body?.document_name;
  if (key && signingNow.has(key)) return inProgress();
  if (visit && (await orderReviewAtSignOff(visit)) === "hold") {
    return { status: 499, result: false, cancelled: true, message: "Not signed." };
  }
  if (key && signingNow.has(key)) return inProgress();
  if (key) signingNow.add(key);
  const send = async (confirm) => {
    try {
      return await blinkscribeapi.put("/v1/document/sign-off", confirm ? { ...body, confirm_visit_mismatch: true } : body);
    } catch (err) {
      if (isSignOffInProgress(err)) return inProgress(err.response.data);
      throw err;
    }
  };
  try {
    try {
      return await send(preConfirmed);
    } catch (err) {
      if (preConfirmed || !isVisitMismatchRefusal(err)) throw err;
      const ok = await requestSignMismatchConsent(mismatchDetailsFromRefusal(err.response.data));
      if (!ok) return { status: 499, result: false, cancelled: true, message: "Not signed." };
      return await send(true);
    }
  } finally {
    if (key) signingNow.delete(key);
  }
};
